/**
 * Contract Intelligence's menu in its app area: Overview and New review;
 * Reviews with the latest few beneath it (each with its status); the
 * library — Documents by counterparty, Templates; and Settings.
 *
 * @module components/apps/contractIntelligence/ContractIntelligenceSidebarNav
 */
import { Link } from "@tanstack/react-router";
import { ClipboardListIcon, FilesIcon, HomeIcon, ListChecksIcon, PlusIcon, Settings2Icon } from "lucide-react";

import { useCiReviews } from "../../../state/contractIntelligence";
import { SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem } from "../../ui/sidebar";
import type { AppAreaNavProps } from "../AppsSidebar";
import { AppAreaNavGroup, AppAreaNavRow } from "../appAreaNavParts";
import { ciReviewIdFromPathname, ciSectionFromPathname, isActive } from "./ciModel";
import { ReviewStatusMark } from "./ciParts";

const RECENT = 5;

export function ContractIntelligenceSidebarNav({ orgSegment, organizationId, pathname, onNavigate }: AppAreaNavProps) {
  const section = ciSectionFromPathname(pathname);
  const openReviewId = ciReviewIdFromPathname(pathname);
  const reviews = useCiReviews(organizationId);
  const list = reviews.read?.kind === "ready" ? reviews.read.value : [];
  const recent = list.slice(0, RECENT);
  if (openReviewId && !recent.some((review) => review.id === openReviewId)) {
    const open = list.find((review) => review.id === openReviewId);
    if (open) recent.push(open);
  }
  const params = { org: orgSegment };
  return (
    <>
      <AppAreaNavGroup>
        <AppAreaNavRow
          data-app-area-row="home"
          active={section === "overview"}
          icon={<HomeIcon />}
          label="Overview"
          render={<Link to="/$org/apps/contract-intelligence" params={params} onClick={onNavigate} />}
        />
        <AppAreaNavRow
          data-app-area-row="new"
          active={section === "new"}
          icon={<PlusIcon />}
          label="New review"
          render={<Link to="/$org/apps/contract-intelligence/new" params={params} onClick={onNavigate} />}
        />
      </AppAreaNavGroup>
      <AppAreaNavGroup label="Reviews">
        <AppAreaNavRow
          data-app-area-row="reviews"
          active={section === "reviews"}
          icon={<ListChecksIcon />}
          label="All reviews"
          count={list.length}
          render={<Link to="/$org/apps/contract-intelligence/reviews" params={params} onClick={onNavigate} />}
        />
        {recent.length > 0 ? (
          <SidebarMenuSub className="me-0 border-sidebar-border/70 pe-0" data-ci-recent-reviews="">
            {recent.map((review) => (
              <SidebarMenuSubItem key={review.id}>
                <SidebarMenuSubButton
                  isActive={review.id === openReviewId}
                  className="h-7 gap-2 text-[13px]"
                  render={
                    <Link
                      to="/$org/apps/contract-intelligence/reviews/$reviewId"
                      params={{ org: orgSegment, reviewId: review.id }}
                      onClick={onNavigate}
                      title={review.title}
                    />
                  }
                >
                  <ReviewStatusMark status={review.status} className={isActive(review.status) ? undefined : "opacity-80"} />
                  <span className="truncate">{review.title}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        ) : null}
      </AppAreaNavGroup>
      <AppAreaNavGroup label="Library">
        <AppAreaNavRow
          data-app-area-row="documents"
          active={section === "documents"}
          icon={<FilesIcon />}
          label="Documents"
          render={<Link to="/$org/apps/contract-intelligence/documents" params={params} onClick={onNavigate} />}
        />
        <AppAreaNavRow
          data-app-area-row="templates"
          active={section === "templates"}
          icon={<ClipboardListIcon />}
          label="Templates"
          render={<Link to="/$org/apps/contract-intelligence/templates" params={params} onClick={onNavigate} />}
        />
      </AppAreaNavGroup>
      <AppAreaNavGroup label="Admin">
        <AppAreaNavRow
          data-app-area-row="settings"
          active={section === "settings"}
          icon={<Settings2Icon />}
          label="Settings"
          render={<Link to="/$org/apps/contract-intelligence/settings" params={params} onClick={onNavigate} />}
        />
      </AppAreaNavGroup>
    </>
  );
}
